import { useApi } from "~/composables/useApi";
import { toDatabaseType } from "@asap/shared/document/document-types.utils";
import type { FieldMessages } from "@asap/shared/document/types";
import type { StoreResponse } from "@asap/shared/schemas/document/base";
import type { z } from "zod";
import { fromFormValues } from "../utils";
import type { DocumentTypeConfig } from "./types";

/**
 * Document type handler
 *
 * Wraps a document type definition with API operations.
 * Provides both static configuration access and runtime methods.
 *
 * TExtraction: Raw OCR output type
 * TData: Validated data type
 * TContext: Context required for operations (e.g., talentId)
 */
export class DocumentTypeHandler<TExtraction, TData, TContext = Record<string, unknown>> {
  constructor(public readonly config: DocumentTypeConfig<TExtraction, TData>) {}

  // Proxy config properties for convenience
  get documentType() {
    return this.config.documentType;
  }
  get databaseType() {
    return toDatabaseType(this.config.documentType);
  }
  get documentName() {
    return this.config.documentName;
  }
  get maxFiles() {
    return this.config.maxFiles;
  }
  get acceptedMimeTypes() {
    return this.config.acceptedMimeTypes;
  }
  get requiredFields() {
    return this.config.requiredFields;
  }
  get formFields() {
    return this.config.formFields;
  }
  get banner() {
    return this.config.banner;
  }
  get transformExtraction() {
    return this.config.transformExtraction;
  }

  /**
   * Extract data from files via OCR
   */
  async extractFromFiles(files: File[], options?: { signal?: AbortSignal }): Promise<TExtraction> {
    const { invoke } = useApi();

    const formData = new FormData();
    files.forEach((file) => formData.append("file", file));

    const response = await invoke({
      // @ts-expect-error - path is dynamic string, not literal
      path: this.config.endpoints.extract,
      method: "POST",
      // @ts-expect-error - body type depends on path
      body: formData,
      signal: options?.signal,
    });

    if (!response.success) {
      throw new Error(response.error.message ?? "Une erreur est survenue lors de l'extraction");
    }

    return parseResponse(this.config.schemas.extractResponse, response.data, this.config.endpoints.extract);
  }

  /**
   * Get field warnings (non-blocking validation)
   */
  async warn(data: Record<string, string>, context: TContext): Promise<FieldMessages<TData>> {
    const { invoke } = useApi();
    const typedData = fromFormValues(data, this.config.formFields);

    const response = await invoke({
      // @ts-expect-error - path is dynamic string, not literal
      path: this.config.endpoints.warn,
      method: "POST",
      // @ts-expect-error - body type depends on path
      body: { data: typedData, context },
    });

    if (!response.success) {
      console.warn("Warning API call failed:", response.error);
      return {};
    }

    return parseResponse(this.config.schemas.warnResponse, response.data, this.config.endpoints.warn);
  }

  /**
   * Validate fields (blocking validation)
   */
  async validateFields(data: Record<string, string>, context: TContext): Promise<FieldMessages<TData>> {
    const { invoke } = useApi();
    const typedData = fromFormValues(data, this.config.formFields);

    const response = await invoke({
      // @ts-expect-error - path is dynamic string, not literal
      path: this.config.endpoints.verify,
      method: "POST",
      // @ts-expect-error - body type depends on path
      body: { data: typedData, context },
    });

    if (!response.success) {
      console.warn("Validation API call failed:", response.error);
      return {};
    }

    return parseResponse(this.config.schemas.validateResponse, response.data, this.config.endpoints.verify);
  }

  /**
   * Store document with validated data
   */
  async store(
    files: File[],
    data: Record<string, string>,
    context: TContext,
    extractedData?: TExtraction,
    replacesDocumentId?: string
  ): Promise<StoreResponse> {
    const { invoke } = useApi();
    const typedData = fromFormValues(data, this.config.formFields);

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("data", JSON.stringify(typedData));
    formData.append("context", JSON.stringify(context));
    if (extractedData) {
      formData.append("extractedData", JSON.stringify(extractedData));
    }
    if (replacesDocumentId) {
      formData.append("replacesDocumentId", replacesDocumentId);
    }

    const response = await invoke({
      // @ts-expect-error - path is dynamic string, not literal
      path: this.config.endpoints.store,
      method: "POST",
      // @ts-expect-error - body type depends on path
      body: formData,
    });

    if (!response.success) {
      throw new Error(response.error.message ?? "Une erreur est survenue lors de l'enregistrement");
    }

    return parseResponse(this.config.schemas.storeResponse, response.data, this.config.endpoints.store);
  }
}

function parseResponse<T>(schema: z.ZodType<T>, data: unknown, endpoint: string): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    console.error(`Schema validation failed for ${endpoint}:`, result.error);
    throw new Error(`Failed to parse response from ${endpoint}`);
  }
  return result.data;
}

/**
 * Type-erased handler for registries and collections
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyDocumentTypeHandler = DocumentTypeHandler<any, any, any>;
